import { useRef } from 'react';
import { useTheme } from '../../contexts';
import { useInlineAutoplay } from './use-inline-autoplay';
import { useChapterLoop } from './use-chapter-loop';
import GuideShot from './GuideShot';
import type { ContentBlock, PhotoComparePair } from '../../data/guide-articles';

/**
 * Draws a guide's content blocks. The one renderer behind the article page,
 * the edit panel's live preview (which IS the article page) and the slideshow
 * popup, so a block added to the parser is drawn the same way everywhere.
 *
 * Paragraphs, list items, table cells and notes arrive as HTML the parser
 * built from the guide's own markdown (lib/parse-guide-mdx.ts), so they go in
 * through dangerouslySetInnerHTML. That text is written by staff, in the repo
 * or through the admin-only editor.
 */

/**
 * Guide videos are generated per theme as `<name>-light.<ext>` / `<name>-dark.<ext>`
 * (see scripts/generate-guide-journeys.ts). MDX references the -light variant; here we
 * swap to the -dark one when the viewer is in dark mode. Videos without a -light/-dark
 * suffix pass through unchanged.
 *
 * Each guide is ONE journey recording; `start`/`end` scope this instance to the
 * chapter for its section, so the section loops just its own step.
 */
export function themedVideoSrc(src: string, theme: string): string {
  return theme === 'dark'
    ? src.replace(/-light\.(mp4|webm)$/, '-dark.$1')
    : src.replace(/-dark\.(mp4|webm)$/, '-light.$1');
}

/** The <source> type for a clip: the recorder writes .webm and .mp4. */
export function videoMime(src: string): string {
  return src.endsWith('.webm') ? 'video/webm' : 'video/mp4';
}

export function GuideVideo({ src, start, end }: { src: string; start?: number; end?: number }) {
  const { theme } = useTheme();
  const themed = themedVideoSrc(src, theme);
  const mime = videoMime(themed);
  const ref = useRef<HTMLVideoElement>(null);
  useInlineAutoplay(ref, themed);
  useChapterLoop(ref, start, end, themed);
  return (
    <div className="blog-article__image-placeholder">
      {/* key forces a reload when the themed source changes */}
      <video
        ref={ref}
        key={themed}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        // `contain`, not `cover`: journey clips are 1.6:1 and cover was shaving
        // the captions off the bottom of the 16:9 box.
        className="w-full h-full object-contain rounded-xl"
      >
        <source src={themed} type={mime} />
      </video>
    </div>
  );
}

/** Side-by-side "shoot it like this, not like that" rows. */
export function PhotoCompare({ pairs }: { pairs: PhotoComparePair[] }) {
  return (
    <div className="guide-compare">
      {pairs.map((pair) => (
        <div key={pair.do.src + pair.dont.src} className="guide-compare__row">
          {(['do', 'dont'] as const).map((side) => (
            <figure key={side} className={`guide-compare__card guide-compare__card--${side}`}>
              <img src={pair[side].src} alt={pair[side].caption} className="guide-compare__img" />
              <figcaption className="guide-compare__caption">
                <span className={`guide-compare__label guide-compare__label--${side}`}>
                  {side === 'do' ? 'Do' : "Don't"}
                </span>
                {pair[side].caption}
              </figcaption>
            </figure>
          ))}
        </div>
      ))}
    </div>
  );
}

function renderBlock(block: ContentBlock, index: number) {
  switch (block.type) {
    case 'paragraph':
      return (
        // eslint-disable-next-line react/no-danger -- parser-built HTML from staff-written guide text
        <p key={`p-${index}`} className="guide-article__paragraph" dangerouslySetInnerHTML={{ __html: block.text }} />
      );
    case 'list':
      return (
        <ul key={`ul-${index}`} className="guide-article__list">
          {block.items.map((item, i) => (
            // eslint-disable-next-line react/no-array-index-key, react/no-danger -- items can repeat; parser-built HTML
            <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ul>
      );
    case 'numberedList':
      return (
        <ol key={`ol-${index}`} className="guide-article__list guide-article__list--numbered">
          {block.items.map((item, i) => (
            // eslint-disable-next-line react/no-array-index-key, react/no-danger -- items can repeat; parser-built HTML
            <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ol>
      );
    case 'video':
      return <GuideVideo key={`vid-${index}`} src={block.src} start={block.start} end={block.end} />;
    case 'photoCompare':
      return <PhotoCompare key={`cmp-${index}`} pairs={block.pairs} />;
    case 'steps':
      return (
        // `start` carries the numbering on when the slideshow splits one
        // <Steps> across slides.
        <ol key={`steps-${index}`} className="guide-steps" start={block.start}>
          {block.steps.map((step, i) => (
            // eslint-disable-next-line react/no-array-index-key -- two steps may share a title
            <li key={i} className="guide-steps__step">
              <div className="guide-steps__title">{step.title}</div>
              {step.content.length > 0 && (
                <div className="guide-steps__body">
                  <GuideBlocks blocks={step.content} />
                </div>
              )}
            </li>
          ))}
        </ol>
      );
    case 'shot':
      return <GuideShot key={`shot-${index}-${block.base}`} base={block.base} alt={block.alt} />;
    case 'note':
      return (
        <aside key={`note-${index}`} className={`guide-note guide-note--${block.tone}`}>
          <GuideBlocks blocks={block.content} />
        </aside>
      );
    case 'table':
      return (
        // The page never scrolls sideways; a wide table scrolls in its own box.
        <div key={`table-${index}`} className="guide-table">
          <table>
            <thead>
              <tr>
                {block.head.map((cell, i) => (
                  // eslint-disable-next-line react/no-array-index-key, react/no-danger -- column order is the identity; parser-built HTML
                  <th key={i} scope="col" dangerouslySetInnerHTML={{ __html: cell }} />
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                // eslint-disable-next-line react/no-array-index-key -- rows can repeat
                <tr key={r}>
                  {row.map((cell, c) => (
                    // eslint-disable-next-line react/no-array-index-key, react/no-danger -- column order is the identity; parser-built HTML
                    <td key={c} dangerouslySetInnerHTML={{ __html: cell }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

interface GuideBlocksProps {
  blocks: ContentBlock[];
}

export default function GuideBlocks({ blocks }: GuideBlocksProps) {
  return <>{blocks.map((block, i) => renderBlock(block, i))}</>;
}
